import type { RemindMeRequest, UnremindMeRequest } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { useParams } from "next/navigation"
import { useEffect } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { HeartFilled } from "@jobteaser/spark/components/Icons/HeartFilled"
import { HeartOutlined } from "@jobteaser/spark/components/Icons/HeartOutlined"

import { SAVE, SAVED } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useRegistrationStatus } from "@/modules/events/hooks/useRegistrationStatus"
import { useRemindMe } from "@/modules/events/hooks/useRemindMe"
import { useUnremindMe } from "@/modules/events/hooks/useUnremindMe"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useNotification } from "@/modules/notification/hooks"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"

export const SaveButton: FunctionComponent = () => {
  const { t } = useTranslation(["event_details", "shared_error"])
  const locale = useLocale()
  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const [subdomain] = hostname.split(".")
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const { registrationStatus, refetch: refetchRegistrationStatus } = useRegistrationStatus({
    eventId: event?.id || "",
    locale,
  })

  const trackEvent = useTrackEvent()

  const { mutate: getRemindMe, isPending: isRemindMeLoading } = useRemindMe()
  const { mutate: getUnremindMe, isPending: isUnremindMeLoading } = useUnremindMe()
  const showNotification = useNotification()

  useEffect(() => {
    if (event && registrationStatus && !registrationStatus.isSaved) {
      trackEvent("event_ad_save_display", {
        event_id: event.id,
        is_visible: true,
        service: "event_details",
        subdomain,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationStatus?.isSaved])

  if (!registrationStatus || !event) {
    return null
  }

  const handleSaveButtonClick = (): void => {
    getRemindMe(
      {
        hostname,
        id: event.id,
        locale,
      } as RemindMeRequest,
      {
        onError: () => {
          showNotification({
            title: t("shared_error.generic"),
            variant: "error",
          })
        },
        onSuccess: () => {
          trackEvent("event_ad_save", {
            context: "event_ad",
            event_ad_id: event.id,
            service: "event_details",
          })
          refetchRegistrationStatus()
        },
      }
    )
  }

  const handleRemoveFromSavedButtonClick = (): void => {
    getUnremindMe(
      {
        hostname,
        id: event.id,
        locale,
      } as UnremindMeRequest,
      {
        onError: () => {
          showNotification({
            title: t("shared_error.generic"),
            variant: "error",
          })
        },
        onSuccess: () => {
          refetchRegistrationStatus()
        },
      }
    )
  }

  return (
    <Button
      data-testid={`event-details-registration-details-${registrationStatus.isSaved ? SAVED : SAVE}-button`}
      disabled={isRemindMeLoading || isUnremindMeLoading}
      icon={registrationStatus.isSaved ? HeartFilled : HeartOutlined}
      onClick={registrationStatus.isSaved ? handleRemoveFromSavedButtonClick : handleSaveButtonClick}
      variant="secondary"
    >
      {t(`event_details.button.${registrationStatus.isSaved ? SAVED : SAVE}`)}
    </Button>
  )
}
